/* Idempotent loader for the CobrowseIO SDK.
 *
 * Why a singleton loader, not the cobrowse vendor IIFE snippet:
 * the vendor snippet is designed to run once at <head> parse time. Inside
 * a React effect it can re-run, and each re-run clobbers the live
 * window.CobrowseIO with a stub-without-.start (see vm-reservation-website
 * PB-896 for the bug class). This loader injects the <script> tag exactly
 * once and resolves a cached promise with the live SDK (or null on failure)
 * for every subsequent call.
 */

declare global {
  interface Window {
    CobrowseIO?: any;
  }
}

const COBROWSE_SCRIPT_URL = 'https://js.cobrowse.io/CobrowseIO.js';

let loadPromise: Promise<unknown | null> | null = null;

export function loadCobrowse(): Promise<unknown | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.CobrowseIO?.start) return Promise.resolve(window.CobrowseIO);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = COBROWSE_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve(window.CobrowseIO ?? null);
    script.onerror = () => {
      // Reset the cached promise so a future call can retry the load instead
      // of being stuck with a permanently-null resolution.
      loadPromise = null;
      resolve(null);
    };
    document.head.appendChild(script);
  });
  return loadPromise;
}

/* Test-only escape hatch — clears the cached promise so unit tests can drive
 * fresh load attempts without re-importing the module. Not exported from the
 * package barrel; consumers should never call this. */
export function __resetCobrowseLoaderForTests() {
  loadPromise = null;
}
